import React from 'react'
import { Navigation, NavigationPageItem, HubId } from 'types/navigation'
import Box from '@mui/material/Box'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Collapse from '@mui/material/Collapse'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Dashboard from '@mui/icons-material/Dashboard'
import PushPin from '@mui/icons-material/PushPin'
import PushPinOutlined from '@mui/icons-material/PushPinOutlined'
import { useTheme, Theme, alpha } from '@mui/material/styles'
import { HUBS } from 'constants/hubs'
import { isOwnerDashboardPath } from 'router/dashboardRoute'
import {
  hubForPath,
  pagesForHub,
  visibleHubs,
} from 'router/hubNavigation'
import { collectPinnablePages, isPinnedRoute, pagesForPins, type PinnablePage } from 'router/sidebarPins'
import {
  NAV_ITEM_HEIGHT_DESKTOP,
  NAV_ITEM_HEIGHT_DESKTOP_CHILD,
  TOUCH_MIN,
} from 'core/theme.tokens'

interface RenderOptions {
  open: boolean
  setOpen: (open: boolean) => void
  location: { pathname: string }
  openDropdowns: { [key: string]: boolean }
  handleNavigation: (path?: string) => void
  handleDropdownToggle: (segment: string) => void
  /** Open the rail and expand this section only (no toggle). */
  expandDropdown: (segment: string) => void
  theme: Theme
  hubMode?: boolean
  pins: string[]
  onTogglePin: (id: string) => void
}

const NAV_PRIMARY_FONT = '0.8125rem'
const NAV_SECONDARY_FONT = '0.6875rem'

/** Hidden labels still take width at opacity 0 and force a horizontal scrollbar on the compact rail. */
function navLabelSx(open: boolean) {
  return {
    opacity: open ? 1 : 0,
    display: open ? 'block' : 'none',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }
}

const itemButtonSx = (open: boolean, highlight: boolean, theme: Theme, accent?: string) => ({
  minHeight: { xs: TOUCH_MIN, sm: NAV_ITEM_HEIGHT_DESKTOP },
  justifyContent: open ? 'initial' : 'center',
  px: open ? 2 : 0,
  mx: 0,
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: 0,
  border: 'none',
  boxSizing: 'border-box' as const,
  // Full-bleed selected fill (no inset left rail).
  backgroundColor: highlight ? theme.palette.primary.dark : 'transparent',
  color: highlight ? theme.palette.primary.contrastText : 'inherit',
  '& .MuiListItemText-root': {
    overflow: 'hidden',
    minWidth: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: NAV_PRIMARY_FONT,
    fontWeight: highlight ? 600 : 500,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiListItemText-secondary': {
    fontSize: NAV_SECONDARY_FONT,
    lineHeight: 1.2,
  },
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    color: highlight ? 'inherit' : accent || 'inherit',
  },
  '&:hover': {
    backgroundColor: highlight
      ? theme.palette.primary.dark
      : accent
        ? `${accent}28`
        : 'rgba(255, 255, 255, 0.1)',
  },
})

const childButtonSx = (open: boolean, pageActive: boolean, theme: Theme) => ({
  minHeight: { xs: TOUCH_MIN - 4, sm: NAV_ITEM_HEIGHT_DESKTOP_CHILD },
  justifyContent: open ? 'initial' : 'center',
  pl: 3.5,
  pr: 2,
  mx: 0,
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: 0,
  border: 'none',
  boxSizing: 'border-box' as const,
  backgroundColor: pageActive ? theme.palette.primary.dark : 'transparent',
  color: pageActive ? theme.palette.primary.contrastText : 'inherit',
  '& .MuiListItemText-root': {
    overflow: 'hidden',
    minWidth: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: NAV_PRIMARY_FONT,
    fontWeight: pageActive ? 600 : 400,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&:hover': {
    backgroundColor: pageActive
      ? theme.palette.primary.dark
      : 'rgba(255, 255, 255, 0.1)',
  },
})

function SidebarPinControl({
  pinned,
  title,
  onToggle,
}: {
  pinned: boolean
  title: string
  onToggle: () => void
}) {
  return (
    <Tooltip title={pinned ? 'Unpin' : 'Pin'} placement='right'>
      <IconButton
        size='small'
        aria-label={pinned ? `Unpin ${title}` : `Pin ${title}`}
        aria-pressed={pinned}
        onClick={event => {
          event.stopPropagation()
          onToggle()
        }}
        sx={{
          color: alpha('#F4F1EB', pinned ? 0.95 : 0.5),
          flexShrink: 0,
          mr: 0.25,
          my: 'auto',
        }}
      >
        {pinned ? <PushPin sx={{ fontSize: 16 }} /> : <PushPinOutlined sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  )
}

function DockSeparator() {
  return (
    <Box
      role='separator'
      aria-hidden
      sx={{
        mx: 1.25,
        my: 0.75,
        borderTop: '2px dashed',
        borderColor: alpha('#F4F1EB', 0.55),
      }}
    />
  )
}

function renderPinnedDock(pages: Navigation, options: RenderOptions): React.ReactNode[] {
  const pinned = pagesForPins(collectPinnablePages(pages), options.pins)
  if (pinned.length === 0) return []

  const { open, theme, handleNavigation, location } = options
  const nodes: React.ReactNode[] = [<DockSeparator key='sidebar-pin-separator' />]

  for (const page of pinned) {
    nodes.push(renderDockPage(page, options, open, theme, location, handleNavigation))
  }

  return nodes
}

function renderDockPage(
  page: PinnablePage,
  options: RenderOptions,
  open: boolean,
  theme: Theme,
  location: { pathname: string },
  handleNavigation: (path?: string) => void,
) {
  const accent = page.hub ? HUBS[page.hub].accent : undefined
  const pageActive = Boolean(
    page.path &&
      (location.pathname === page.path || location.pathname.startsWith(`${page.path}/`)),
  )

  return (
    <ListItem key={`pin-${page.id}`} disablePadding sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip title={open ? '' : page.title} placement='right' arrow>
          <ListItemButton
            onClick={() => {
              if (page.href) {
                window.open(page.href, '_blank', 'noopener,noreferrer')
                return
              }
              handleNavigation(page.segment)
            }}
            sx={itemButtonSx(open, pageActive, theme, accent)}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.5 : 0,
                justifyContent: 'center',
                color: pageActive ? 'inherit' : accent || 'inherit',
              }}
            >
              {page.icon}
            </ListItemIcon>
            <ListItemText primary={page.title} sx={navLabelSx(open)} />
          </ListItemButton>
        </Tooltip>
      </Box>
      {open ? (
        <SidebarPinControl pinned title={page.title} onToggle={() => options.onTogglePin(page.id)} />
      ) : null}
    </ListItem>
  )
}

const renderNavigationItem = (item: NavigationPageItem, index: number, options: RenderOptions) => {
  const { open, location, openDropdowns, handleNavigation, handleDropdownToggle, theme } = options
  const isCurrentPage = location.pathname === `/${item.segment}`
  const hasChildren = item.children && item.children.length > 0
  const isDropdownOpen = openDropdowns[item.segment || '']

  const isChildSelected =
    hasChildren &&
    item.children?.some(child => {
      if (child.kind === 'header' || child.kind === 'divider') return false
      const pageChild = child as NavigationPageItem
      const childId = `${item.segment}/${pageChild.segment}`
      return location.pathname === `/${childId}` && !options.pins.includes(childId)
    })

  const shouldHighlight = isCurrentPage || isChildSelected

  return (
    <React.Fragment key={item.segment || `item-${index}`}>
      <ListItem disablePadding sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={open ? '' : item.title || ''} placement='right' arrow>
            <ListItemButton
              onClick={() => {
                if (item.href) {
                  window.open(item.href, '_blank', 'noopener,noreferrer')
                  return
                }
                if (hasChildren) {
                  if (!open) {
                    options.setOpen(true)
                    options.expandDropdown(item.segment || '')
                  } else {
                    handleDropdownToggle(item.segment || '')
                  }
                } else {
                  handleNavigation(item.segment)
                }
              }}
              sx={itemButtonSx(open, Boolean(shouldHighlight), theme)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  justifyContent: 'center',
                  color: 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} sx={navLabelSx(open)} />
              {hasChildren &&
                open &&
                (isDropdownOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </Tooltip>
        </Box>
        {!hasChildren && open && (item.segment || item.href) ? (
          <SidebarPinControl
            pinned={options.pins.includes(item.segment || item.href || '')}
            title={item.title}
            onToggle={() => options.onTogglePin(item.segment || item.href || '')}
          />
        ) : null}
      </ListItem>
      {hasChildren && (
        <Collapse in={isDropdownOpen && open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {item.children?.map((child, childIndex) => {
              if (child.kind === 'header' || child.kind === 'divider') return null
              const childId = `${item.segment}/${child.segment}`
              const childActive = location.pathname === `/${childId}`

              return (
                <ListItem
                  key={`${item.segment}-child-${childIndex}`}
                  disablePadding
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemButton
                      onClick={() => handleNavigation(childId)}
                      sx={childButtonSx(open, childActive, theme)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 0,
                          justifyContent: 'center',
                          color: 'inherit',
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText primary={child.title} sx={navLabelSx(open)} />
                    </ListItemButton>
                  </Box>
                  {open ? (
                    <SidebarPinControl
                      pinned={options.pins.includes(childId)}
                      title={child.title}
                      onToggle={() => options.onTogglePin(childId)}
                    />
                  ) : null}
                </ListItem>
              )
            })}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  )
}

const renderHubDrawer = (pages: Navigation, options: RenderOptions) => {
  const { open, location, openDropdowns, handleNavigation, handleDropdownToggle, theme } = options
  const activeHub = hubForPath(pages, location.pathname)
  const routeIsPinned = isPinnedRoute(collectPinnablePages(pages), options.pins, location.pathname)
  const hubs = visibleHubs(pages)
  const dashboardActive = isOwnerDashboardPath(location.pathname)

  const docsItem = pages.find(
    item => item.kind !== 'header' && item.kind !== 'divider' && Boolean((item as NavigationPageItem).href),
  ) as NavigationPageItem | undefined

  const nodes: React.ReactNode[] = [
    <ListItem key='hub-dashboard' disablePadding sx={{ display: 'block' }}>
      <Tooltip title={open ? '' : 'Dashboard'} placement='right' arrow>
        <ListItemButton
          onClick={() => handleNavigation('admin')}
          sx={itemButtonSx(open, dashboardActive, theme)}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 1.5 : 0,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            <Dashboard fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Dashboard' sx={navLabelSx(open)} />
        </ListItemButton>
      </Tooltip>
    </ListItem>,
  ]

  for (const hubId of hubs) {
    const def = HUBS[hubId as HubId]
    const hubPages = pagesForHub(pages, hubId)
    if (hubPages.length === 0) continue
    const hubKey = `hub-${hubId}`
    const highlight = activeHub === hubId && !routeIsPinned
    const expanded = Boolean(openDropdowns[hubKey])
    const Icon = def.Icon

    nodes.push(
      <React.Fragment key={hubKey}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={open ? '' : def.title} placement='right' arrow>
            <ListItemButton
              onClick={() => {
                // Categories only expand/collapse — never navigate.
                // Opening the rail first when collapsed still does not change the route.
                if (!open) {
                  options.setOpen(true)
                  options.expandDropdown(hubKey)
                } else {
                  handleDropdownToggle(hubKey)
                }
              }}
              sx={itemButtonSx(open, highlight, theme, def.accent)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 0,
                  justifyContent: 'center',
                  color: highlight ? 'inherit' : def.accent,
                }}
              >
                <Icon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary={def.title} sx={navLabelSx(open)} />
              {open ? (
                expanded ? (
                  <ExpandLess fontSize='small' />
                ) : (
                  <ExpandMore fontSize='small' />
                )
              ) : null}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <Collapse in={expanded && open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {hubPages.map(page => {
              const pageActive =
                location.pathname === page.path || location.pathname.startsWith(`${page.path}/`)
              return (
                <ListItem key={page.path} disablePadding sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemButton
                      onClick={() => {
                        if (page.href) {
                          window.open(page.href, '_blank', 'noopener,noreferrer')
                          return
                        }
                        handleNavigation(page.segment)
                      }}
                      sx={childButtonSx(open, pageActive, theme)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1.5 : 0,
                          justifyContent: 'center',
                          color: pageActive ? 'inherit' : def.accent,
                          '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                        }}
                      >
                        {page.icon}
                      </ListItemIcon>
                      <ListItemText primary={page.title} sx={navLabelSx(open)} />
                    </ListItemButton>
                  </Box>
                  {open ? (
                    <SidebarPinControl
                      pinned={options.pins.includes(page.segment)}
                      title={page.title}
                      onToggle={() => options.onTogglePin(page.segment)}
                    />
                  ) : null}
                </ListItem>
              )
            })}
          </List>
        </Collapse>
      </React.Fragment>,
    )

    if (hubId === 'tools') {
      nodes.push(...renderPinnedDock(pages, options))
    }
  }

  if (!hubs.includes('tools')) {
    nodes.push(...renderPinnedDock(pages, options))
  }

  if (docsItem?.href) {
    nodes.push(<Divider key='hub-docs-divider' sx={{ my: 0.5, opacity: 1 }} />)
    nodes.push(
      <ListItem key='hub-docs' disablePadding sx={{ display: 'block' }}>
        <Tooltip title={open ? '' : docsItem.title} placement='right' arrow>
          <ListItemButton
            onClick={() => window.open(docsItem.href, '_blank', 'noopener,noreferrer')}
            sx={itemButtonSx(open, false, theme)}
          >
            <ListItemIcon
              sx={{ minWidth: 0, mr: open ? 3 : 0, justifyContent: 'center' }}
            >
              {docsItem.icon}
            </ListItemIcon>
            <ListItemText primary={docsItem.title} sx={navLabelSx(open)} />
          </ListItemButton>
        </Tooltip>
      </ListItem>,
    )
  }

  return nodes
}

export const mapPagesToDrawer = (pages: Navigation, options: Omit<RenderOptions, 'theme'> & { theme?: Theme; hubMode?: boolean }) => {
  const theme = useTheme()
  const optionsWithTheme: RenderOptions = { ...options, theme, hubMode: options.hubMode }

  if (options.hubMode) {
    return renderHubDrawer(pages, optionsWithTheme)
  }

  const { open } = optionsWithTheme
  const rendered: React.ReactNode[] = []
  let toolsInsertAt = -1

  pages.forEach((item, index) => {
    if (item.kind === 'header') {
      if (!open) return
      rendered.push(
        <React.Fragment key={`header-${index}`}>
          <Typography
            variant='caption'
            sx={{
              px: 3,
              pt: 1,
              pb: 0.75,
              display: 'block',
              color: alpha('#F4F1EB', 0.55),
              fontSize: '0.6875rem',
              letterSpacing: '0.08em',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mt: index === 0 ? 0 : 1,
            }}
          >
            {item.title}
          </Typography>
          <Divider sx={{ opacity: 0.12 }} />
        </React.Fragment>,
      )
      return
    }

    if (item.kind === 'divider') {
      rendered.push(<Divider key={`divider-${item.key || index}`} sx={{ my: 0.5, opacity: 1 }} />)
      return
    }

    rendered.push(renderNavigationItem(item as NavigationPageItem, index, optionsWithTheme))
    if (item.title === 'Tools' && item.children && item.children.length > 0) {
      toolsInsertAt = rendered.length
    }
  })

  const pinned = renderPinnedDock(pages, optionsWithTheme)
  if (pinned.length > 0) {
    if (toolsInsertAt >= 0) rendered.splice(toolsInsertAt, 0, ...pinned)
    else rendered.push(...pinned)
  }

  return rendered
}
